import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import toast, { Toaster } from 'react-hot-toast';
import axios from "axios";
import useAppStore from '../appStore';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from "jwt-decode";

const schema = yup.object().shape({
    email: yup.string().email().required('Please enter email address'),
    password: yup.string()
        .min(8, 'Password must be at least 8 characters')
        .max(20, 'Password must not exceed 20 characters')
        .notOneOf([yup.ref('email'), null], 'Password must not be the same as your email address')
        .required('Please enter password'),
})


export default function Login() {
    const navigate = useNavigate();
    const baseURL = process.env.REACT_APP_API_URL;
    const { updateToken, updateUserId, updateUserEmail, updateUserName, updateUserType } = useAppStore();

    const { register, handleSubmit, formState: { errors } } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            email: '',
            password: '',
            confirmPassword: '',
        }
    });

    const onSubmitNew = async (data) => {

        console.log(data);
        let posturl = baseURL + "/login";

        await axios
            .post(posturl, {
                email: data.email,
                password: data.password,
            })
            .then(function (res) {
                // console.log(res.data.user);
                updateToken(res.data.token);
                updateUserEmail(res.data.user.email);
                updateUserName(res.data.user.name);
                updateUserType(res.data.user.type);
                updateUserId(res.data.user.id);
                navigate("/");
            })
            .catch(function (error) {
                toast.error(error || 'Something went wrong');
                console.log(error);
            });

    };



    return (
        <>
            <Toaster />
            <div className="flex flex-col md:flex-row items-center justify-around min-h-screen px-4 md:px-0">
                {/* Logo and Information Section */}
                <div className="py-10 md:py-0 text-center">
                    <div className="text-3xl md:text-5xl font-semibold mb-6">
                        <a href="/" className="flex items-center justify-center">
                            <img src="/logo.png" className="h-24" alt="CodingMSTR Logo" />
                        </a>
                        <div className="bg-gradient-to-r from-cyan-600 via-purple-600 to-green-600 text-transparent bg-clip-text animate-gradient">
                            CodingMSTR
                        </div>
                    </div>
                    <p className="text-sm md:text-base text-gray-600">
                        Turn your projects into profit — join as a seller today!
                    </p>
                    <p className="text-sm md:text-base text-gray-600 mt-2">
                        Keep <span className="font-semibold">90% of every sale</span> and reach buyers who value your work.
                    </p>
                </div>

                {/* Form Section */}
                <form
                    onSubmit={handleSubmit(onSubmitNew)}
                    className="w-full max-w-md p-6 md:p-8 bg-white shadow-md rounded-xl border border-slate-300"
                >
                    <h2 className="text-2xl font-bold text-center text-cyan-600 mb-6">Login</h2>

                    {/* Email Field */}
                    <div className="mb-5">
                        <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                            Email
                        </label>
                        <input
                            type="email"
                            id="email"
                            placeholder="Enter your email"
                            className="w-full px-4 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                            {...register("email")}
                        />
                        {errors?.email && (
                            <p className="text-red-500 text-xs mt-1">{errors.email?.message}</p>
                        )}
                    </div>

                    {/* Password Field */}
                    <div className="mb-5">
                        <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                            Password
                        </label>
                        <input
                            type="password"
                            id="password"
                            placeholder="Enter your password"
                            className="w-full px-4 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                            {...register("password")}
                        />
                        {errors?.password && (
                            <p className="text-red-500 text-xs mt-1">{errors.password?.message}</p>
                        )}
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        className="w-full py-2 text-sm font-semibold text-white bg-cyan-600 hover:bg-cyan-500 rounded-md"
                    >
                        LOGIN
                    </button>

                    {/* Google Login */}
                    <div className="mt-6">
                        <GoogleLogin
                            onSuccess={async (credentialResponse) => {
                                try {
                                    const USER_CREDENTIAL = jwtDecode(credentialResponse.credential);
                                    const res = await axios.post(`${baseURL}/google-auth`, {
                                        name: USER_CREDENTIAL.name,
                                        email: USER_CREDENTIAL.email,
                                        credential: credentialResponse.credential,
                                        clientId: credentialResponse.clientId,
                                    });

                                    if (res?.data?.token && res?.data?.user) {
                                        updateToken(res.data.token);
                                        updateUserEmail(res.data.user.email);
                                        updateUserName(res.data.user.name);
                                        updateUserType(res.data.user.type);
                                        updateUserId(res.data.user.id);
                                        window.location.href = '/';
                                    } else {
                                        toast.error('Unexpected response from the server.');
                                    }
                                } catch (error) {
                                    const errorMessage = error.response?.data?.message || 'Google login failed. Please try again.';
                                    toast.error(errorMessage);
                                }
                            }}
                            onError={() => toast.error('Google login failed. Please try again.')}
                        />
                    </div>

                    {/* Register Link */}
                    <div className="mt-4 text-center">
                        <p className="text-xs md:text-sm text-gray-500">
                            Don't have an account?{' '}
                            <a href="https://codingmstr.com/register" className="text-cyan-600 font-semibold hover:underline">
                                Request Now
                            </a>
                        </p>
                    </div>
                </form>
            </div>

            <hr className='mt-10' />
            <footer className="bg-blue-50 border-slate-200">
                <div className="mx-auto w-full max-w-screen-xl p-4 py-6 lg:py-8">
                    <div className="md:flex md:justify-between">
                        <div className="mb-6 md:mb-0 md:max-w-[20vw]">
                            <a href="https://codingmstr.com/" className="flex items-center">
                                <img src="/logo.png" className="h-8 me-3" alt="CodingMSTR Logo" />
                                <span
                                    className="self-center text-2xl font-semibold whitespace-nowrap dark:text-white"
                                >CodingMSTR</span
                                >
                            </a>
                            <p className="text-justify mt-5">
                                Download Source Code for Various Projects Discover a comprehensive
                                collection of free and paid source code tailored to meet your project
                                needs, all in one convenient online platform.
                            </p>
                        </div>
                        <div className="grid grid-cols-2 gap-8 sm:gap-6 sm:grid-cols-3">
                            <div>
                                <h2
                                    className="mb-6 text-sm font-semibold text-gray-900 uppercase dark:text-white"
                                >
                                    Resources
                                </h2>
                                <ul className="text-gray-500 dark:text-gray-400 font-medium">
                                    <li className="mb-4">
                                        <a href="https://codingmstr.com/" className="hover:underline">Home</a>
                                    </li>
                                    <li className="mb-4">
                                        <a
                                            href="https://play.google.com/store/apps/dev?id=5534507549519790618"
                                            className="hover:underline">Android Apps</a
                                        >
                                    </li>
                                    <li className="mb-4">
                                        <a href="https://codingmstr.com/pricing" className="hover:underline">Pricing</a>
                                    </li>
                                    <li>
                                        <a href="https://codingmstr.com/contact" className="hover:underline">Contact</a>
                                    </li>
                                </ul>
                            </div>
                            <div>
                                <h2
                                    className="mb-6 text-sm font-semibold text-gray-900 uppercase dark:text-white"
                                >
                                    Follow me
                                </h2>
                                <ul className="text-gray-500 dark:text-gray-400 font-medium">
                                    <li className="mb-4">
                                        <a href="https://github.com/ipraveenkmr" className="hover:underline"
                                        >Github</a
                                        >
                                    </li>
                                    <li className="mb-4">
                                        <a
                                            href="https://www.youtube.com/@codingmstr"
                                            className="hover:underline">YouTube</a
                                        >
                                    </li>
                                    <li className="mb-4">
                                        <a
                                            href="https://www.instagram.com/codingmstr/"
                                            className="hover:underline">Instagram</a
                                        >
                                    </li>
                                    <li>
                                        <a
                                            href="https://www.facebook.com/procodingmaster"
                                            className="hover:underline">Facebook</a
                                        >
                                    </li>
                                </ul>
                            </div>
                            <div>
                                <h2
                                    className="mb-6 text-sm font-semibold text-gray-900 uppercase dark:text-white"
                                >
                                    Legal
                                </h2>
                                <ul className="text-gray-500 dark:text-gray-400 font-medium">
                                    <li className="mb-4">
                                        <a href="https://codingmstr.com/privacy-policy" className="hover:underline"
                                        >Privacy Policy</a
                                        >
                                    </li>
                                    <li className="mb-4">
                                        <a href="https://codingmstr.com/terms" className="hover:underline">Terms &amp; Conditions</a
                                        >
                                    </li>
                                    <li className="mb-4">
                                        <a href="https://codingmstr.com/license" className="hover:underline">License</a>
                                    </li>
                                    <li>
                                        <a href="https://codingmstr.com/refund-policy" className="hover:underline">Refund Policy</a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <hr className="my-6 border-gray-200 sm:mx-auto dark:border-gray-700 lg:my-8" />
                    <div className="sm:flex sm:items-center sm:justify-between">
                        <span className="text-sm text-gray-500 sm:text-center dark:text-gray-400"
                        >© {new Date().getFullYear()}
                            <a href="https://codingmstr.com/" className="hover:underline">CodingMSTR</a
                            >. All Rights Reserved.
                        </span>
                    </div>
                </div>
            </footer>
        </>
    );
}